/**
 * Skeleton Loading Components for ScopeOps Platform
 * Provides consistent loading states across the application
 */

import React from 'react';

/**
 * Base skeleton element with animation
 */
export const Skeleton = ({ className = '', style = {} }) => (
  <div 
    className={`animate-pulse bg-gray-200 rounded ${className}`}
    style={style}
    data-testid="skeleton"
  />
);

/**
 * Text line skeleton
 */
export const SkeletonLine = ({ width = '100%', height = '1rem', className = '' }) => (
  <Skeleton 
    className={className}
    style={{ width, height }}
  />
);

/**
 * Circle skeleton (for avatars)
 */
export const SkeletonCircle = ({ size = '2.5rem', className = '' }) => (
  <Skeleton 
    className={`rounded-full ${className}`}
    style={{ width: size, height: size }}
  />
);

/**
 * Card skeleton
 */
export const SkeletonCard = ({ className = '', children }) => (
  <div className={`bg-white rounded-lg border p-4 ${className}`}>
    {children || (
      <>
        <SkeletonLine width="60%" height="1.25rem" className="mb-3" />
        <SkeletonLine width="100%" height="0.875rem" className="mb-2" />
        <SkeletonLine width="80%" height="0.875rem" />
      </>
    )}
  </div>
);

/**
 * Table row skeleton
 */
export const SkeletonTableRow = ({ columns = 5 }) => (
  <tr className="border-b">
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <SkeletonLine 
          width={i === 0 ? '80%' : '60%'} 
          height="0.875rem" 
        />
      </td>
    ))}
  </tr>
);

/**
 * Table skeleton with multiple rows
 */
export const SkeletonTable = ({ rows = 5, columns = 5 }) => (
  <div className="bg-white rounded-lg border overflow-hidden">
    <div className="bg-gray-50 border-b px-4 py-3">
      <div className="flex gap-4">
        {Array.from({ length: columns }).map((_, i) => (
          <SkeletonLine 
            key={i} 
            width="80px" 
            height="0.75rem" 
          />
        ))}
      </div>
    </div>
    <table className="w-full">
      <tbody>
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonTableRow key={i} columns={columns} />
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * KPI Card skeleton
 */
export const SkeletonKPICard = ({ className = '' }) => (
  <div className={`bg-white rounded-lg border p-4 ${className}`}>
    <SkeletonLine width="40%" height="0.75rem" className="mb-2" />
    <SkeletonLine width="60%" height="1.5rem" />
  </div>
);

/**
 * KPI Grid skeleton
 */
export const SkeletonKPIGrid = ({ count = 5, className = '' }) => (
  <div className={`grid grid-cols-2 md:grid-cols-${Math.min(count, 5)} gap-4 ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonKPICard key={i} />
    ))}
  </div>
);

/**
 * Metric card skeleton for right panes
 */
export const SkeletonMetricCard = ({ className = '' }) => (
  <div className={`bg-gray-100 rounded-lg p-3 ${className}`}>
    <div className="flex items-center justify-between mb-2">
      <SkeletonCircle size="1rem" />
    </div>
    <SkeletonLine width="70%" height="1.25rem" className="mb-1" />
    <SkeletonLine width="50%" height="0.75rem" />
  </div>
);

/**
 * Right pane content skeleton
 */
export const SkeletonPaneContent = () => (
  <div className="p-4 space-y-6">
    {/* Header */}
    <div className="flex items-center gap-3">
      <SkeletonCircle size="2.5rem" />
      <div className="flex-1">
        <SkeletonLine width="60%" height="1.25rem" className="mb-2" />
        <SkeletonLine width="40%" height="0.75rem" />
      </div>
    </div>
    
    {/* Status and progress */}
    <div className="flex items-center gap-4">
      <SkeletonLine width="80px" height="1.5rem" className="rounded-full" />
      <div className="flex-1">
        <SkeletonLine width="100%" height="0.5rem" className="rounded-full" />
      </div>
    </div>
    
    {/* Metric cards grid */}
    <div className="grid grid-cols-2 gap-3">
      <SkeletonMetricCard />
      <SkeletonMetricCard />
      <SkeletonMetricCard />
      <SkeletonMetricCard />
    </div>
    
    {/* Timeline section */}
    <div className="bg-gray-50 rounded-lg p-3">
      <SkeletonLine width="80px" height="0.75rem" className="mb-3" />
      <div className="grid grid-cols-3 gap-2">
        <div>
          <SkeletonLine width="40px" height="0.625rem" className="mb-1" />
          <SkeletonLine width="80px" height="0.875rem" />
        </div>
        <div>
          <SkeletonLine width="40px" height="0.625rem" className="mb-1" />
          <SkeletonLine width="80px" height="0.875rem" />
        </div>
        <div>
          <SkeletonLine width="40px" height="0.625rem" className="mb-1" />
          <SkeletonLine width="80px" height="0.875rem" />
        </div>
      </div>
    </div>
    
    {/* List items */}
    <div className="space-y-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
          <SkeletonCircle size="1.5rem" />
          <SkeletonLine width="60%" height="0.875rem" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Dashboard widget skeleton
 */
export const SkeletonWidget = ({ height = '200px', className = '' }) => (
  <div className={`bg-white rounded-lg border p-4 ${className}`} style={{ minHeight: height }}>
    <div className="flex items-center justify-between mb-4">
      <SkeletonLine width="120px" height="1rem" />
      <SkeletonLine width="60px" height="0.75rem" />
    </div>
    <div className="space-y-3">
      <SkeletonLine width="100%" height="0.875rem" />
      <SkeletonLine width="90%" height="0.875rem" />
      <SkeletonLine width="70%" height="0.875rem" />
    </div>
  </div>
);

/**
 * Form field skeleton
 */
export const SkeletonFormField = ({ className = '' }) => (
  <div className={`space-y-1.5 ${className}`}>
    <SkeletonLine width="80px" height="0.75rem" />
    <SkeletonLine width="100%" height="2.25rem" className="rounded-lg" />
  </div>
);

/**
 * Full page loading skeleton
 */
export const SkeletonPage = ({ title = true, kpis = true, table = true }) => (
  <div className="space-y-6">
    {title && (
      <div>
        <SkeletonLine width="200px" height="1.75rem" className="mb-2" />
        <SkeletonLine width="300px" height="1rem" />
      </div>
    )}
    {kpis && <SkeletonKPIGrid count={5} />}
    {table && <SkeletonTable rows={8} columns={6} />}
  </div>
);

/**
 * Tree list skeleton for hierarchy views
 */
export const SkeletonTreeList = ({ rows = 8 }) => (
  <div className="border border-gray-200 rounded-lg bg-white p-2 space-y-2">
    {Array.from({ length: rows }).map((_, i) => (
      <div 
        key={i} 
        className="flex items-center gap-3 p-2"
        style={{ paddingLeft: `${(i % 3) * 24 + 12}px` }}
      >
        <Skeleton className="w-4 h-4 rounded" />
        <Skeleton className="w-16 h-5 rounded" />
        <Skeleton className="flex-1 h-4 rounded" style={{ maxWidth: '200px' }} />
        <Skeleton className="w-12 h-4 rounded" />
      </div>
    ))}
  </div>
);

export default {
  Skeleton,
  SkeletonLine,
  SkeletonCircle,
  SkeletonCard,
  SkeletonTableRow,
  SkeletonTable,
  SkeletonKPICard,
  SkeletonKPIGrid,
  SkeletonMetricCard,
  SkeletonPaneContent,
  SkeletonWidget,
  SkeletonFormField,
  SkeletonPage,
  SkeletonTreeList,
};
